import React from 'react';
import {
    Row,
    Col,
    Input,
    Select,
    Empty,
    Spin
} from 'antd';
import {
    SearchOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {
    PLANTEI_DARK,
    WHITE,
    LIGHT_GRAY,
    BORDER_GRAY,
    GREE_3,
    GREE_1
} from '../utils/colors';
import ProductCard from "../components/shop/ProductCard";
import PaginatedList from "../components/PaginatedList";
import ENDPOINTS from "../constants/endpoints";
import {useShop} from "../contexts/ShopContext";


const ShopContainer = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 40px 24px;

    @media (max-width: 768px) {
        padding: 24px 16px;
    }
`;

// Sidebar
const SidebarSection = styled.div`
    margin-bottom: 24px;
`;

const SidebarTitle = styled.h4`
    font-size: 18px;
    font-weight: 600;
    color: ${PLANTEI_DARK};
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid ${GREE_1};
`;

const CategoryList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`;

const CategoryItem = styled.li`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid ${BORDER_GRAY};
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
        color: ${GREE_1};
    }

    &:last-child {
        border-bottom: none;
    }
`;

const CategoryName = styled.span`
    font-size: 14px;
    color: ${props => props.active ? GREE_1 : PLANTEI_DARK};
    font-weight: ${props => props.active ? '600' : '400'};
    transition: color 0.3s;

    ${CategoryItem}:hover & {
        color: ${GREE_1};
    }
`;

const PriceDisplay = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    color: ${PLANTEI_DARK};
    margin-top: 8px;
`;

// Top Controls
const TopControls = styled.div`
    display: flex;
    gap: 16px;
    margin-bottom: 24px;
    flex-wrap: wrap;
    align-items: center;

    @media (max-width: 576px) {
        flex-direction: column;
    }
`;

const SearchWrapper = styled.div`
    flex: 1;
    min-width: 200px;
`;

const SortWrapper = styled.div`
    min-width: 180px;
    background: ${LIGHT_GRAY};
    border-radius: 6px;
    padding: 4px 12px;
    display: flex;
    align-items: center;
    gap: 8px;
`;

// ========================
// COMPONENT
// ========================


export default function ShopHome() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Derivar filtros diretamente da URL
    const searchTerm = searchParams.get('search') || '';
    const sortBy = searchParams.get('sortBy') || 'default';
    const selectedCategory = searchParams.get('category') ? Number(searchParams.get('category')) : null;

    const {categories, loadingCategories} = useShop();

    // Atualizar URL quando filtros mudam
    const updateURL = (overrides = {}) => {
        const params = {
            category: selectedCategory,
            search: searchTerm,
            sortBy,
            ...overrides,
        };
        const newParams = new URLSearchParams();
        if (params.category) newParams.set('category', params.category);
        if (params.search) newParams.set('search', params.search);
        if (params.sortBy && params.sortBy !== 'default') newParams.set('sortBy', params.sortBy);
        setSearchParams(newParams, {replace: true});
    };

    const handleDetail = (product) => {
        navigate(`/area-do-associado/loja/produtos/${product.slug}`);
    };

    const handleCategoryClick = (catId) => {
        const newCategory = selectedCategory === catId ? null : catId;
        updateURL({category: newCategory});
    };

    const handleSearch = (value) => {
        updateURL({search: value});
    };

    const handleSortChange = (value) => {
        updateURL({sortBy: value});
    };

    // Build params for product request
    const productParams = {
        category: selectedCategory,
        search: searchTerm,
        sortBy: sortBy,
    };

    return (
        <div>

            {/* Shop Content */}
            <ShopContainer>
                <Row gutter={[32, 24]}>
                    {/* Sidebar */}
                    <Col xs={24} lg={6}>
                        {/* Categories */}
                        <SidebarSection>
                            <SidebarTitle>Categorias</SidebarTitle>
                            {loadingCategories ? (
                                <Spin size="small"/>
                            ) : (
                                <CategoryList>
                                    <CategoryItem
                                        onClick={() => handleCategoryClick(null)}
                                    >
                                        <CategoryName
                                            active={selectedCategory === null}>
                                            Todas as categorias
                                        </CategoryName>
                                    </CategoryItem>
                                    {categories.map(cat => (
                                        <CategoryItem
                                            key={cat.id}
                                            onClick={() => handleCategoryClick(cat.id)}
                                        >
                                            <CategoryName
                                                active={selectedCategory === cat.id}>
                                                {cat.name}
                                            </CategoryName>
                                        </CategoryItem>
                                    ))}
                                </CategoryList>
                            )}
                        </SidebarSection>
                    </Col>

                    {/* Main Content */}
                    <Col xs={24} lg={18}>
                        {/* Top Controls */}
                        <TopControls>
                            <SearchWrapper>
                                <Input.Search
                                    key={searchTerm}
                                    placeholder="Buscar produtos..."
                                    prefix={<SearchOutlined/>}
                                    size="large"
                                    allowClear
                                    defaultValue={searchTerm}
                                    onSearch={handleSearch}
                                />
                            </SearchWrapper>

                            <SortWrapper>
                                <span style={{
                                    fontSize: 13,
                                    whiteSpace: 'nowrap',
                                    color: '#666'
                                }}>
                                    Ordenar:
                                </span>
                                <Select
                                    value={sortBy}
                                    onChange={handleSortChange}
                                    variant="borderless"
                                    style={{flex: 1}}
                                    options={[
                                        {value: 'default', label: 'Padrão'},
                                        {value: 'name', label: 'Nome'},
                                        {
                                            value: 'price_asc',
                                            label: 'Menor preço'
                                        },
                                        {
                                            value: 'price_desc',
                                            label: 'Maior preço'
                                        },
                                    ]}
                                />
                            </SortWrapper>


                        </TopControls>

                        {/* Products */}
                        <PaginatedList
                            request={{
                                url: ENDPOINTS.ECOMMERCE_PRODUCT_PUBLIC,
                                params: productParams,
                                dependence: [selectedCategory, searchTerm],
                            }}
                            pageSize={10}
                            showTotal={false}
                            emptyComponent={
                                <Empty
                                    description="Nenhum produto encontrado"
                                    style={{padding: '80px 0'}}
                                />
                            }
                        >
                            {(products) => {

                                if (products.length === 0) {
                                    return (
                                        <Empty
                                            description="Nenhum produto encontrado"
                                            style={{padding: '80px 0'}}
                                        />
                                    );
                                }

                                return (
                                    <Row gutter={[16, 16]}>
                                        {products.map(product => (
                                            <Col key={product.id} xs={24}
                                                 sm={12}
                                                 lg={8}>
                                                <ProductCard
                                                    product={product}
                                                    onDetail={handleDetail}

                                                />
                                            </Col>
                                        ))}
                                    </Row>
                                );
                            }}
                        </PaginatedList>
                    </Col>
                </Row>
            </ShopContainer>
        </div>
    );
}